/**
 * LandingNavBar — shared responsive nav for LandingPage, ProPage, ReferralPage.
 *
 * Desktop (>900px): logo left, children (nav links + CTAs) right — unchanged layout.
 * Mobile (≤900px): logo left, hamburger right → slide-in drawer with mobileMenu content.
 *
 * Clicking any <a> inside the drawer automatically closes it.
 * Export MobileNav* primitives for use in mobileMenu.
 *
 * Accessibility
 * ─────────────
 * - The skip-to-content link is rendered globally by <AppLayout>, so this
 *   component no longer ships its own — having two competing skip links on
 *   the same page caused the focused-state of one to paint over the logo.
 * - Closed drawer is removed from the tab order via the `inert` attribute,
 *   not just `aria-hidden`. (`aria-hidden` does not remove tab stops.)
 * - Drawer open moves focus into the drawer; Esc closes and returns focus
 *   to the hamburger.
 */

import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { landingTokens as T } from '@codex/theme';
import LanguageSwitcher from '../../../components/LanguageSwitcher';
import { LandingNav, LandingNavInner, LandingNavLogo, LandingNavLinks } from './LandingPage.styled';
import { VellumLogo } from '../../../components/brand';

// ── Hamburger button ──────────────────────────────────────────────────────────

const HamburgerBtn = styled.button`
  display: none;
  background: none;
  border: 1px solid ${T.borderStrong};
  border-radius: 6px;
  cursor: pointer;
  padding: 8px 10px;
  gap: 5px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  @media (max-width: 900px) {
    display: flex;
  }

  &:focus-visible {
    outline: 2px solid ${T.borderFocus};
    outline-offset: 2px;
  }
`;

const Bar = styled.span<{ $open: boolean; $i: number }>`
  display: block;
  width: 20px;
  height: 2px;
  background: ${T.text2};
  border-radius: 2px;
  transition:
    transform 250ms ease,
    opacity 250ms ease;
  ${({ $open, $i }) => $open && $i === 0 && 'transform: translateY(7px) rotate(45deg);'}
  ${({ $open, $i }) => $open && $i === 1 && 'opacity: 0; transform: scaleX(0);'}
  ${({ $open, $i }) => $open && $i === 2 && 'transform: translateY(-7px) rotate(-45deg);'}
`;

// ── Overlay + Drawer ──────────────────────────────────────────────────────────

const Overlay = styled.div<{ $open: boolean }>`
  display: none;
  @media (max-width: 900px) {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    z-index: 98;
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    pointer-events: ${({ $open }) => ($open ? 'all' : 'none')};
    transition: opacity 200ms;
  }
`;

const Drawer = styled.nav<{ $open: boolean }>`
  display: none;
  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: min(300px, 88vw);
    background: ${T.bg1};
    border-left: 1px solid ${T.borderStrong};
    z-index: 99;
    padding: 80px 24px 32px;
    overflow-y: auto;
    transform: ${({ $open }) => ($open ? 'translateX(0)' : 'translateX(100%)')};
    transition: transform 280ms ease;
  }
`;

const DrawerFooter = styled.div`
  margin-top: auto;
  padding-top: 24px;
  border-top: 1px solid ${T.borderStrong};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DrawerFooterLabel = styled.span`
  font-size: 13px;
  color: ${T.text3};
`;

// ── Mobile menu primitives (exported for use in mobileMenu prop) ──────────────

export const MobileNavLink = styled.a`
  font-size: 16px;
  color: ${T.text2};
  text-decoration: none;
  padding: 14px 0;
  border-bottom: 1px solid ${T.borderStrong};
  display: block;
  transition: color 150ms;
  &:hover {
    color: ${T.cream};
  }

  &:focus-visible {
    outline: 2px solid ${T.borderFocus};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

export const MobileNavRouterLink = styled(Link)`
  font-size: 16px;
  color: ${T.text2};
  text-decoration: none;
  padding: 14px 0;
  border-bottom: 1px solid ${T.borderStrong};
  display: block;
  transition: color 150ms;
  &:hover {
    color: ${T.cream};
  }

  &:focus-visible {
    outline: 2px solid ${T.borderFocus};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

export const MobileNavRouterLinkGold = styled(Link)`
  font-size: 16px;
  color: ${T.gold};
  text-decoration: none;
  padding: 14px 0;
  border-bottom: 1px solid ${T.borderStrong};
  display: block;
  transition: opacity 150ms;
  &:hover {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px solid ${T.borderFocus};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

/**
 * Spacer-only divider — no visible line needed since MobileNav* links already
 * have border-bottom. Using a line here creates a double-bar effect.
 */
export const MobileNavDivider = styled.div`
  margin: 8px 0;
`;

export const MobileNavCta = styled(Link)`
  display: block;
  text-align: center;
  background: ${T.gold};
  color: ${T.textOnGold};
  font-size: 15px;
  font-weight: 500;
  padding: 13px 24px;
  border-radius: 8px;
  text-decoration: none;
  margin-top: 8px;
  transition: opacity 150ms;
  &:hover {
    opacity: 0.88;
  }

  &:focus-visible {
    outline: 2px solid ${T.borderFocus};
    outline-offset: 2px;
  }
`;

export const MobileNavSecondary = styled(Link)`
  display: block;
  text-align: center;
  background: rgba(255, 255, 255, 0.06);
  color: ${T.text};
  font-size: 15px;
  font-weight: 400;
  padding: 13px 24px;
  border-radius: 8px;
  border: 1px solid ${T.borderStrong};
  text-decoration: none;
  margin-top: 8px;
  transition: background 150ms;
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:focus-visible {
    outline: 2px solid ${T.borderFocus};
    outline-offset: 2px;
  }
`;

// ── Component ─────────────────────────────────────────────────────────────────

interface LandingNavBarProps {
  /** Desktop nav content — rendered inside LandingNavLinks (hidden on mobile). */
  children: React.ReactNode;
  /** Mobile drawer content — MobileNav* primitives stacked vertically. */
  mobileMenu: React.ReactNode;
}

export function LandingNavBar({ children, mobileMenu }: LandingNavBarProps) {
  const [open, setOpen] = useState(false);
  const drawerRef = useRef<HTMLElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  // Close drawer when viewport grows past breakpoint
  useEffect(() => {
    const handler = () => {
      if (window.innerWidth > 900) setOpen(false);
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Lock body scroll while drawer is open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Toggle the `inert` attribute on the closed drawer so its links/buttons
  // are removed from the tab order. `aria-hidden` alone does not do this.
  useEffect(() => {
    const drawer = drawerRef.current;
    if (!drawer) return;
    if (open) {
      drawer.removeAttribute('inert');
    } else {
      drawer.setAttribute('inert', '');
    }
  }, [open]);

  // Move focus into the drawer when it opens, and back to the hamburger on
  // Escape. The drawer's first focusable child is found by query rather than
  // by ref because the drawer content is supplied by the caller.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      const drawer = drawerRef.current;
      if (!drawer) return;
      const focusable = drawer.querySelector<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled])',
      );
      focusable?.focus();
    }, 60);
    const escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        hamburgerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', escHandler);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', escHandler);
    };
  }, [open]);

  // Close drawer on any link click inside it
  const handleDrawerClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('a')) setOpen(false);
  };

  return (
    <>
      {/* Skip-to-content link is rendered globally by <AppLayout>. */}
      <LandingNav>
        <LandingNavInner>
          <LandingNavLogo to="/" aria-label="Vellum home">
            <VellumLogo iconHeight={42} color="#D9D9D9" />
          </LandingNavLogo>

          {/* Desktop — hidden at ≤900px via LandingNavLinks media query */}
          <LandingNavLinks aria-label="Primary">{children}</LandingNavLinks>

          {/* Mobile hamburger — visible at ≤900px only */}
          <HamburgerBtn
            ref={hamburgerRef}
            type="button"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            aria-controls="landing-mobile-drawer"
            onClick={() => setOpen((o) => !o)}
          >
            <Bar $open={open} $i={0} />
            <Bar $open={open} $i={1} />
            <Bar $open={open} $i={2} />
          </HamburgerBtn>
        </LandingNavInner>
      </LandingNav>

      <Overlay $open={open} onClick={() => setOpen(false)} aria-hidden />

      <Drawer
        id="landing-mobile-drawer"
        ref={drawerRef}
        $open={open}
        aria-label="Mobile navigation"
        onClick={handleDrawerClick}
      >
        {mobileMenu}

        {/* Language switcher pinned to the bottom of every drawer */}
        <DrawerFooter>
          <DrawerFooterLabel>Language</DrawerFooterLabel>
          <LanguageSwitcher variant="icon" />
        </DrawerFooter>
      </Drawer>
    </>
  );
}
